import { FC } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { CompetenciesCompetencySettingsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { EditionWindow } from 'modules/performance_process/components/review/EditionWindow/EditionWindow'

import { AudienceSection } from './AudienceSection'
import { BasicInformationSection } from './BasicInformationSection'

const CompetencySettings: FC = () => {
  const { competencyId } = useParams<{ competencyId: string }>()
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSection = searchParams.get('section') ?? 'basic_information'
  const { data, loading, error } = useQuery(CompetenciesCompetencySettingsPageDocument, {
    variables: { id: Number(competencyId), companyId: company.id },
  })
  const competency = data?.competencies.competency

  const isLegacyJobCatalogEnabled =
    data?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const isJobCatalogTreeEnabled = !isLegacyJobCatalogEnabled

  if (isJobCatalogTreeEnabled && competencyId) {
    return <Navigate replace to={navigation.competencies.competencyId.fullPath({ competencyId })} />
  }

  const section = (() => {
    if (!competency) {
      return null
    }

    switch (selectedSection) {
      case 'basic_information':
        return <BasicInformationSection competency={competency} />
      case 'audience':
        return <AudienceSection competency={competency} />
      default:
        return null
    }
  })()

  return (
    <ResponsivePage.Body variant="narrow" loading={loading} error={error}>
      <Grid variant="fixed">
        <Box gridColSpan={{ s: 4 }} gap="s8" width="s300">
          <EditionWindow
            label={i18n.t('competencies_v3.settings.basic_information.title')}
            value="basic_information"
            selected={selectedSection === 'basic_information'}
            icon="file-filled"
            onChange={() => setSearchParams({ section: 'basic_information' })}
          />
          {!isJobCatalogTreeEnabled && (
            <EditionWindow
              label={i18n.t('competencies_v3.settings.audience.title')}
              value="audience"
              selected={selectedSection === 'audience'}
              icon="manager"
              onChange={() => setSearchParams({ section: 'audience' })}
            />
          )}
        </Box>
        <Box gridColSpan={{ s: 6 }}>{section}</Box>
      </Grid>
    </ResponsivePage.Body>
  )
}

export default CompetencySettings
